import { useCallback, useMemo } from "react";

import Decimal from "decimal.js";

import useGetPortfolioPlanClassificationById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanClassificationById";
import { Entry } from "@fartherfinance/frontend/api/Investment/types";
import { AssetClassCategory } from "@fartherfinance/frontend/api/PerformanceGroups/requests/getHoldings";
import {
  PortfolioPlanId,
  RequestConfig,
} from "@fartherfinance/frontend/api/Types";

import { AssetClassification } from "@src/multiCustodian/components/Portfolios/AssetAllocationPieChart/AssetAllocationPieChart";

import { TransitionProposalDetailsData } from "./types";
import useGetVirtualPortfolioPlanMetrics from "./useGetVirtualPortfolioPlanMetrics";
import useGetVirtualPortfolioPlanPositions from "./useGetVirtualPortfolioPlanPositions";

export default (auth: RequestConfig | null) => {
  const getPositionsByPortfolioPlanId =
    useGetVirtualPortfolioPlanPositions(auth);
  const getVirtualPortfolioPlanMetrics =
    useGetVirtualPortfolioPlanMetrics(auth);

  const useGetData = useCallback(
    (
      portfolioPlanId: PortfolioPlanId,
      providedEntries?: Entry[]
    ): TransitionProposalDetailsData => {
      /**************************************
       * 1. get portfolio classification data
       */
      const {
        data: portfolioPlanClassificationData,
        isLoading: isLoadingClassifications,
        hasError: hasErrorClassifications,
      } = useGetPortfolioPlanClassificationById(portfolioPlanId, auth);

      /********************************************
       * 2 if entries were not provided, fetch them
       */
      const entries =
        providedEntries || getPositionsByPortfolioPlanId(portfolioPlanId);

      /***********************************
       * 3. get portfolio performance data
       */
      const {
        portfolioStats,
        isLoadingPortfolioStats,
        hasPortfolioStatsError,
      } = getVirtualPortfolioPlanMetrics(entries);

      /**********************************
       * 4. format the data for the chart
       */
      const classifications: AssetClassification[] = useMemo(() => {
        if (portfolioPlanClassificationData) {
          return Object.entries(portfolioPlanClassificationData)
            .reduce<AssetClassification[]>((acc, [key, entry]) => {
              return acc.concat({
                value: new Decimal(entry.weight).times(100).toNumber(),
                label: AssetClassCategory.parse(key),
              });
            }, [])
            .sort((a, b) => (a.value < b.value ? 1 : -1));
        }
        return [];
      }, [portfolioPlanClassificationData]);

      /**********************
       * 5. return everything
       */
      return {
        isLoadingClassifications,

        hasErrorClassifications,

        classifications,

        isLoadingPortfolioStats,

        hasErrorPortfolioStats: hasPortfolioStatsError,

        portfolioStats,

        invalidPositions: [],
      };
    },
    [auth, getPositionsByPortfolioPlanId, getVirtualPortfolioPlanMetrics]
  );
  return useGetData;
};
